import { Sparkles, HeartHandshake } from "lucide-react";
import Section, { PageHero } from "@/components/Section";
import Reveal from "@/components/Reveal";
import CTAButton from "@/components/CTAButton";
import { ourStory } from "@/data/content";
import { useBrand } from "@/context/BrandContext";
import media from "@/config/media";

export default function OurStoryPage() {
  const brand = useBrand();
  return (
    <>
      <PageHero eyebrow="Our story" title={ourStory.headline} intro={ourStory.opening} />

      <Section id="story">
        <div className="grid gap-12 lg:grid-cols-[1fr_0.8fr] lg:items-start">
          <Reveal className="space-y-5">
            {ourStory.paragraphs.map((p) => (
              <p key={p.slice(0, 30)} className="text-sm leading-relaxed text-cocoa/70 sm:text-base">
                {p}
              </p>
            ))}
            <blockquote className="mt-8 rounded-[28px] bg-sageSoft p-6 sm:p-7">
              <p className="font-heading text-xl font-bold leading-snug text-cocoa sm:text-2xl">
                {ourStory.belief}
              </p>
            </blockquote>
            <p className="text-sm leading-relaxed text-cocoa/70 sm:text-base">{ourStory.closing}</p>
            <p className="font-heading text-base font-semibold italic text-coralInk">
              {ourStory.signature}
            </p>
            <div className="pt-4">
              <CTAButton to="/get-started" data-testid="story-cta">
                Start your child’s plan
              </CTAButton>
            </div>
          </Reveal>

          <Reveal delay={120} className="lg:sticky lg:top-28">
            <figure>
              <div className="mbm-tilt overflow-hidden rounded-[32px] shadow-softLg">
                <img
                  src={media.founderAndChild}
                  alt="A mother serving a home-cooked lunch to her young son"
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              </div>
              <figcaption className="mt-5 text-xs leading-relaxed text-cocoa/75">
                Placeholder image. A photograph of Urvashi and Simba will replace this.
              </figcaption>
            </figure>
            <div className="mt-6 rounded-card bg-surface p-6 shadow-soft">
              <p className="mbm-eyebrow">What we are building</p>
              <p className="mt-3 font-heading text-lg font-bold leading-snug">{brand.tagline}</p>
              <p className="mt-2 text-sm leading-relaxed text-cocoa/70">
                {brand.product.planLabel} — {brand.product.description.toLowerCase()}.
              </p>
            </div>
          </Reveal>
        </div>
      </Section>

      <Section
        id="mission"
        tone="creamDeep"
        eyebrow="Our mission"
        title={ourStory.mission.headline}
        intro={ourStory.mission.copy}
      >
        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {ourStory.mission.points.map((p, i) => (
            <Reveal key={p.title} delay={i * 90}>
              <div className="mbm-card mbm-card-hover h-full p-6 sm:p-7" data-testid={`mission-card-${i}`}>
                <span className="flex h-12 w-12 items-center justify-center rounded-[20px] bg-amber text-coralInk">
                  <Sparkles className="h-5 w-5" aria-hidden="true" />
                </span>
                <h3 className="mt-5 font-heading text-xl font-black tracking-[-0.01em]">{p.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-cocoa/70">{p.copy}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section
        id="purpose"
        tone="forest"
        eyebrow="Beyond food"
        title={ourStory.purpose.headline}
        intro={ourStory.purpose.copy}
      >
        <div className="mt-12 grid gap-5 md:grid-cols-2">
          {ourStory.purpose.points.map((p, i) => (
            <Reveal key={p.title} delay={i * 90}>
              <div
                className="h-full rounded-card bg-white p-6 shadow-soft sm:p-7"
                data-testid={`purpose-card-${i}`}
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-[20px] bg-amber text-cocoa">
                  <HeartHandshake className="h-5 w-5" aria-hidden="true" />
                </span>
                <h3 className="mt-5 font-heading text-xl font-black text-cocoa">{p.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-cocoa/80">{p.copy}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-10">
          <CTAButton to="/mothers-supporting-mothers" variant="onDark" data-testid="story-purpose-cta">
            How we support mothers
          </CTAButton>
        </div>
      </Section>
    </>
  );
}
